'use strict';
import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
	Box,
	Button,
	Checkbox,
	CircularProgress,
	List,
	ListItem,
	ListItemIcon,
	ListItemText,
	Typography,
} from '@mui/material';
import { useAllRolesQuery } from '@/redux/services/portal/admin.api';
import './AddScopeModal.scss';

const AddRoleToUserModal = ({
	open,
	onCancel,
	onConfirm,
	getRoleName,
	existingRoleNames,
	isSubmitting,
}) => {
	const [selectedRoleNames, setSelectedRoleNames] = useState(new Set());
	const normalizedExisting = useMemo(
		() => new Set(existingRoleNames.map((name) => (name || '').toLowerCase()).filter(Boolean)),
		[existingRoleNames]
	);

	useEffect(() => {
		if (open) {
			setSelectedRoleNames(new Set(existingRoleNames));
		}
	}, [open, existingRoleNames]);

	const rolesResult = useAllRolesQuery(undefined, { skip: !open });

	const results = useMemo(() => {
		const items = rolesResult.data || [];
		const mapped = items.map((item) => ({
			...item,
			name: getRoleName(item),
			description: item.description || item.Description,
		}));
		return mapped
			.filter((item) => getRoleName(item))
			.sort((left, right) => (getRoleName(left) || '').localeCompare(getRoleName(right) || ''));
	}, [rolesResult.data, getRoleName]);

	const handleToggleRole = (role) => {
		const roleName = getRoleName(role);
		if (!roleName) return;
		setSelectedRoleNames((prev) => {
			const next = new Set(prev);
			if (next.has(roleName)) {
				next.delete(roleName);
			} else {
				next.add(roleName);
			}
			return next;
		});
	};

	const handleConfirm = () => {
		if (isSubmitting) return;
		const selectedRoles = results.filter((role) => selectedRoleNames.has(getRoleName(role)));
		const selectedRoleNamesArray = Array.from(selectedRoleNames.values());
		const selectedNormalized = new Set(
			selectedRoleNamesArray.map((name) => (name || '').toLowerCase()).filter(Boolean)
		);
		const rolesToAdd = Array.from(selectedNormalized).filter((name) => !normalizedExisting.has(name));
		const rolesToRemove = Array.from(normalizedExisting).filter((name) => !selectedNormalized.has(name));
		if (rolesToAdd.length === 0 && rolesToRemove.length === 0) return;

		onConfirm({ selectedRoles, selectedRoleNames: selectedRoleNamesArray });
	};

	const selectedNormalized = useMemo(
		() => new Set(
			Array.from(selectedRoleNames.values())
				.map((name) => (name || '').toLowerCase())
				.filter(Boolean)
		),
		[selectedRoleNames]
	);
	const rolesToAdd = useMemo(
		() => Array.from(selectedNormalized).filter((name) => !normalizedExisting.has(name)),
		[selectedNormalized, normalizedExisting]
	);
	const rolesToRemove = useMemo(
		() => Array.from(normalizedExisting).filter((name) => !selectedNormalized.has(name)),
		[selectedNormalized, normalizedExisting]
	);
	const hasChanges = rolesToAdd.length > 0 || rolesToRemove.length > 0;
	const updateLabel = hasChanges
		? `Update Roles (+${rolesToAdd.length} / -${rolesToRemove.length})`
		: 'Update Roles';

	return (
		<Box className="AddScopeModal" sx={{ mt: 1 }}>
			{rolesResult.isFetching ? (
				<Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
					<CircularProgress size={20} />
				</Box>
			) : null}
			{!rolesResult.isFetching ? (
				<List dense className="AddScopeModalList">
					{results.map((role) => (
						<ListItem
							button
							key={getRoleName(role)}
							selected={selectedRoleNames.has(getRoleName(role))}
							onClick={() => {
								if (!isSubmitting) {
									handleToggleRole(role);
								}
							}}
						>
							<ListItemIcon>
								<Checkbox
									edge="start"
									checked={selectedRoleNames.has(getRoleName(role))}
									tabIndex={-1}
									disableRipple
									disabled={isSubmitting}
								/>
							</ListItemIcon>
							<ListItemText
								primary={getRoleName(role)}
								secondary={role.description || role.Description}
							/>
						</ListItem>
					))}
					{results.length === 0 ? (
						<ListItem>
							<ListItemText primary="No roles available" />
						</ListItem>
					) : null}
				</List>
			) : null}
			<Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
				<Button onClick={onCancel} sx={{ mr: 1 }}>
					Cancel
				</Button>
				<Button variant="contained" onClick={handleConfirm} disabled={!hasChanges || isSubmitting}>
					{isSubmitting ? 'Saving...' : updateLabel}
				</Button>
			</Box>
		</Box>
	);
};

AddRoleToUserModal.propTypes = {
	open: PropTypes.bool.isRequired,
	onCancel: PropTypes.func.isRequired,
	onConfirm: PropTypes.func.isRequired,
	getRoleName: PropTypes.func.isRequired,
	existingRoleNames: PropTypes.arrayOf(PropTypes.string),
	isSubmitting: PropTypes.bool,
};

AddRoleToUserModal.defaultProps = {
	existingRoleNames: [],
	isSubmitting: false,
};

export default AddRoleToUserModal;
